import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { procurementDetailsQuery } from '@/features/subcontractor-bids/queries';
import {
  useDeleteProcurement,
  useUpdateProcurement,
} from '@/features/subcontractor-bids/mutations';
import { Field, FieldControl, FieldError, FieldLabel } from '@/ui/components/form/field';
import { Button } from '@/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Form } from '@/ui/components/form';
import { Card } from '@/ui/components/card';
import { H3 } from '@/ui/components/heading';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId/settings/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { procurementId, workspaceId, projectId } = Route.useParams();
  const navigate = useNavigate();

  const { data: procurement } = useSuspenseQuery(
    procurementDetailsQuery(workspaceId, procurementId),
  );

  const [displayName, setDisplayName] = useState(procurement.displayName);
  const [seenProcurement, setSeenProcurement] = useState(procurement);
  if (procurement !== seenProcurement) {
    setSeenProcurement(procurement);
    setDisplayName(procurement.displayName);
  }
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const updateProcurement = useUpdateProcurement(procurementId);
  const deleteProcurement = useDeleteProcurement(procurementId);

  return (
    <Form
      onFormSubmit={() => updateProcurement.mutate({ displayName })}
      className="max-w-xl w-full flex flex-col gap-4"
    >
      <Card className="space-y-4 p-4">
        <H3 className="-mt-1">{t({ en: 'Info', nb: 'Info' })}</H3>
        <Field>
          <FieldLabel>{t({ en: 'Name', nb: 'Navn' })}</FieldLabel>
          <FieldControl
            value={displayName}
            onValueChange={setDisplayName}
            placeholder={t({ en: 'e.g. HVAC package', nb: 'f.eks. VVS-pakke' })}
            maxLength={60}
            disabled={updateProcurement.isPending}
            required
          />
          <FieldError />
        </Field>
      </Card>
      <Button
        type="submit"
        pending={updateProcurement.isPending}
        disabled={updateProcurement.isPending}
        className="ml-auto"
      >
        {t({ en: 'Save', nb: 'Lagre' })}
      </Button>
      <Card className="p-4">
        <H3 className="-mt-1">{t({ en: 'Danger zone', nb: 'Faresone' })}</H3>
        <p className="text-sm text-muted mt-2 mb-4">
          {t({
            en: "Deleting a procurement removes it and unlinks its offers. This can't be undone.",
            nb: 'Å slette en anskaffelse fjerner den og kobler fra tilbudene. Dette kan ikke angres.',
          })}
        </p>
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogTrigger
            render={
              <Button variant="destructive">
                {t({ en: 'Delete procurement', nb: 'Slett anskaffelse' })}
              </Button>
            }
          />
          <AlertDialogPopup>
            <AlertDialogTitle>
              {t({ en: 'Delete procurement?', nb: 'Slette anskaffelse?' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t({
                en: 'This permanently removes this procurement pack.',
                nb: 'Dette fjerner denne anskaffelsespakken permanent.',
              })}
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel />
              <Button
                variant="destructive"
                onClick={() =>
                  deleteProcurement.mutate(undefined, {
                    onSuccess: () => {
                      setDeleteDialogOpen(false);
                      navigate({
                        to: '/project/$projectId/workspaces/$workspaceId/subcontractor-bids',
                        params: { projectId, workspaceId },
                      });
                    },
                  })
                }
                pending={deleteProcurement.isPending}
                disabled={deleteProcurement.isPending}
              >
                {t({ en: 'Delete procurement', nb: 'Slett anskaffelse' })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </Card>
    </Form>
  );
}
